import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Upload, Lock, User, ArrowUp, ArrowDown, Image as ImageIcon, Film, Dog, Star, Pencil, Check, X, PawPrint, MessageSquare, Newspaper } from "lucide-react";
import KennelAdminLitters from "./KennelAdminLitters";
import KennelAdminReviews from "./KennelAdminReviews";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";
const ADMIN_LOGIN = "Martyna";
const ADMIN_PASSWORD = "Martyna";

const KennelAdminGallery = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("gallery");

  useEffect(() => {
    const saved = sessionStorage.getItem("kennel_admin_logged");
    if (saved === "true") setIsLoggedIn(true);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.login === ADMIN_LOGIN && loginForm.password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      sessionStorage.setItem("kennel_admin_logged", "true");
    } else {
      setLoginError("Nieprawidłowe dane logowania");
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm bg-[#141416] border border-[#2A2A2D] p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-[#F5F5F5] text-lg tracking-wider">Panel Admina</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-[10px] text-[#B8B8B8] tracking-wider block mb-1">LOGIN</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="text" value={loginForm.login} onChange={e => setLoginForm(p => ({ ...p, login: e.target.value }))}
                  className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm pl-10 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  data-testid="admin-gallery-login-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[#B8B8B8] tracking-wider block mb-1">HASŁO</label>
              <input type="password" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm px-4 py-3 focus:border-[#D4AF37] outline-none"
                data-testid="admin-gallery-password-input" />
            </div>
            {loginError && <p className="text-red-400 text-xs">{loginError}</p>}
            <button type="submit" className="w-full bg-[#D4AF37] text-[#0B0B0D] py-3 text-sm font-semibold tracking-wider hover:bg-[#E8C547] transition-colors"
              data-testid="admin-gallery-login-btn">ZALOGUJ</button>
          </form>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: "gallery", label: "GALERIA", icon: ImageIcon },
    { id: "dogs", label: "PSY", icon: Dog },
    { id: "mioty", label: "MIOTY", icon: PawPrint },
    { id: "videos", label: "FILMY", icon: Film },
    { id: "opinie", label: "OPINIE", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0D] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-[#F5F5F5] text-xl tracking-wider">Panel Zarządzania</h1>
          <a href="/" target="_blank" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">STRONA</a>
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wider transition-colors ${tab === t.id ? "bg-[#D4AF37] text-[#0B0B0D]" : "border border-[#2A2A2D] text-[#B8B8B8] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          <a href="/admin/blog" className="flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wider border border-[#2A2A2D] text-[#B8B8B8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            data-testid="tab-blog-link">
            <Newspaper className="w-4 h-4" /> Z ŻYCIA HODOWLI
          </a>
        </div>
        {tab === "gallery" && <GalleryTab />}
        {tab === "dogs" && <DogsTab />}
        {tab === "mioty" && <KennelAdminLitters embedded />}
        {tab === "videos" && <VideosTab />}
        {tab === "opinie" && <KennelAdminReviews embedded />}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// GALLERY TAB
// ═══════════════════════════════════════
const GalleryTab = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [pending, setPending] = useState([]); // [{file, preview, label}]
  const fileInputRef = useRef(null);

  const fetchPhotos = async () => {
    try { const r = await fetch(`${API}/gallery`); setPhotos(await r.json()); } catch {}
  };
  useEffect(() => { fetchPhotos(); }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setPending(files.map(file => ({ file, preview: URL.createObjectURL(file), label: "" })));
    fileInputRef.current.value = "";
  };

  const confirmUpload = async () => {
    setUploading(true);
    for (const item of pending) {
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("label", item.label.trim() || item.file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
      try { await fetch(`${API}/admin/gallery/upload`, { method: "POST", body: fd }); } catch {}
    }
    setUploading(false);
    setPending([]);
    fetchPhotos();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć to zdjęcie?")) return;
    await fetch(`${API}/admin/gallery/${id}`, { method: "DELETE" });
    setPhotos(p => p.filter(x => x.id !== id));
  };

  const handleMove = async (idx, dir) => {
    const arr = [...photos];
    const t = dir === "up" ? idx - 1 : idx + 1;
    if (t < 0 || t >= arr.length) return;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    setPhotos(arr);
    await fetch(`${API}/admin/gallery/reorder`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order: arr.map(p => p.id) })
    });
  };

  const saveLabel = async (id) => {
    await fetch(`${API}/admin/gallery/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: editLabel })
    });
    setPhotos(p => p.map(x => x.id === id ? { ...x, label: editLabel } : x));
    setEditingId(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-[#B8B8B8]/40 text-sm">{photos.length} zdjęć · <span className="text-[#D4AF37]/70">kliknij opis pod zdjęciem, aby go zmienić</span></span>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="flex items-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold tracking-wider hover:bg-[#E8C547] disabled:opacity-50"
          data-testid="gallery-upload-btn">
          <Upload className="w-4 h-4" /> {uploading ? "WGRYWANIE..." : "DODAJ ZDJĘCIA"}
        </button>
        <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleFileSelect} />
      </div>

      {/* Upload modal — set caption per photo before upload */}
      {pending.length > 0 && (
        <div className="fixed inset-0 bg-[#0B0B0D]/85 z-50 flex items-center justify-center p-4" data-testid="gallery-upload-modal">
          <div className="bg-[#141416] border border-[#2A2A2D] w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#2A2A2D] sticky top-0 bg-[#141416]">
              <h3 className="text-[#F5F5F5] text-sm tracking-wider">Dodaj opisy do zdjęć ({pending.length})</h3>
              <button onClick={() => setPending([])} className="text-[#B8B8B8] hover:text-[#F5F5F5]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {pending.map((item, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <img src={item.preview} alt="" className="w-20 h-20 object-cover border border-[#2A2A2D] shrink-0" />
                  <div className="flex-1">
                    <label className="text-[10px] text-[#B8B8B8]/50 tracking-wider block mb-1">OPIS ZDJĘCIA</label>
                    <input value={item.label} onChange={e => setPending(p => p.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                      placeholder="np. Aisza — owczarek niemiecki długowłosy"
                      className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm px-3 py-2 focus:border-[#D4AF37] outline-none"
                      data-testid={`upload-caption-${i}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 p-5 border-t border-[#2A2A2D] sticky bottom-0 bg-[#141416]">
              <button onClick={() => setPending([])} className="px-5 py-2.5 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:text-[#F5F5F5]">ANULUJ</button>
              <button onClick={confirmUpload} disabled={uploading} data-testid="gallery-confirm-upload"
                className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold tracking-wider py-2.5 hover:bg-[#E8C547] disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? "WGRYWANIE..." : `WGRAJ ${pending.length} ZDJĘĆ`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        <AnimatePresence>
          {photos.map((photo, idx) => (
            <motion.div key={photo.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }} className="group relative bg-[#141416] border border-[#2A2A2D] overflow-hidden">
              <div className="aspect-square relative">
                <img src={photo.src} alt={photo.label} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all" />
                <div className="absolute top-2 left-2 bg-[#0B0B0D]/70 text-[#D4AF37] text-[10px] px-2 py-1 font-mono">{idx + 1}</div>
                <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                  <button onClick={() => handleMove(idx, "up")} disabled={idx === 0}
                    className="w-8 h-8 bg-[#1A1A1D]/90 border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => handleMove(idx, "down")} disabled={idx === photos.length - 1}
                    className="w-8 h-8 bg-[#1A1A1D]/90 border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(photo.id)}
                    className="w-8 h-8 bg-red-900/80 border border-red-700/50 flex items-center justify-center text-red-300 hover:bg-red-800"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="px-2 py-2">
                {editingId === photo.id ? (
                  <div className="flex flex-col gap-1.5">
                    <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveLabel(photo.id); if (e.key === "Escape") setEditingId(null); }}
                      placeholder="Wpisz opis zdjęcia"
                      className="w-full bg-[#0B0B0D] border border-[#D4AF37] text-[#F5F5F5] text-xs px-2 py-1.5 focus:outline-none"
                      autoFocus data-testid={`gallery-edit-input-${idx}`} />
                    <div className="flex gap-1.5">
                      <button onClick={() => saveLabel(photo.id)} data-testid={`gallery-save-label-${idx}`}
                        className="flex-1 flex items-center justify-center gap-1 bg-[#D4AF37] text-[#0B0B0D] text-[10px] font-semibold py-1 hover:bg-[#E8C547]">
                        <Check className="w-3 h-3" /> ZAPISZ
                      </button>
                      <button onClick={() => setEditingId(null)}
                        className="px-2 bg-[#1A1A1D] border border-[#2A2A2D] text-[#B8B8B8] hover:text-[#F5F5F5]"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setEditingId(photo.id); setEditLabel(photo.label); }}
                    data-testid={`gallery-edit-label-${idx}`}
                    className="w-full flex items-center justify-between gap-1 text-left group/edit">
                    <span className="text-[11px] text-[#B8B8B8]/70 truncate group-hover/edit:text-[#F5F5F5]">{photo.label || "Bez opisu"}</span>
                    <Pencil className="w-3 h-3 text-[#D4AF37]/60 group-hover/edit:text-[#D4AF37] shrink-0" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {uploading && <UploadOverlay />}
    </div>
  );
};

// ═══════════════════════════════════════
// DOGS TAB
// ═══════════════════════════════════════
const DogsTab = () => {
  const [dogs, setDogs] = useState([]);
  const [selectedDog, setSelectedDog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDogs = async () => {
    try { const r = await fetch(`${API}/dogs`); setDogs(await r.json()); } catch {}
  };
  useEffect(() => { fetchDogs(); }, []);

  const handleUploadPhoto = async (e) => {
    if (!selectedDog) return;
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await fetch(`${API}/admin/dogs/${selectedDog.id}/upload-photo`, { method: "POST", body: fd });
        const data = await r.json();
        setSelectedDog(prev => ({ ...prev, gallery: data.gallery }));
      } catch {}
    }
    setUploading(false);
    fileInputRef.current.value = "";
    fetchDogs();
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!selectedDog || !window.confirm("Usunąć to zdjęcie?")) return;
    const r = await fetch(`${API}/admin/dogs/${selectedDog.id}/photo`, {
      method: "DELETE", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ photo_url: photoUrl })
    });
    const data = await r.json();
    setSelectedDog(prev => ({ ...prev, gallery: data.gallery }));
    fetchDogs();
  };

  const setMainPhoto = async (photoUrl) => {
    if (!selectedDog) return;
    await fetch(`${API}/admin/dogs/${selectedDog.id}/main-photo`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ photo_url: photoUrl })
    });
    setSelectedDog(prev => ({ ...prev, image_url: photoUrl }));
    fetchDogs();
  };

  return (
    <div>
      {!selectedDog ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {dogs.map(dog => (
            <button key={dog.id} onClick={() => setSelectedDog(dog)}
              className="bg-[#141416] border border-[#2A2A2D] p-4 text-left hover:border-[#D4AF37] transition-colors group"
              data-testid={`dog-card-${dog.name.toLowerCase()}`}>
              <div className="flex items-center gap-4">
                <img src={dog.image_url} alt={dog.name} className="w-16 h-16 object-cover rounded" />
                <div>
                  <h3 className="text-[#F5F5F5] font-medium group-hover:text-[#D4AF37] transition-colors">{dog.name}</h3>
                  <p className="text-[#B8B8B8]/50 text-xs">{dog.gallery?.length || 0} zdjęć w galerii</p>
                  <p className="text-[#B8B8B8]/30 text-[10px]">{dog.registered_name}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedDog(null)} className="text-[#B8B8B8] hover:text-[#D4AF37] text-sm">← Wróć</button>
              <h2 className="text-[#F5F5F5] text-lg">{selectedDog.name}</h2>
              <span className="text-[#B8B8B8]/40 text-xs">({selectedDog.gallery?.length || 0} zdjęć)</span>
            </div>
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold tracking-wider hover:bg-[#E8C547] disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "WGRYWANIE..." : "DODAJ ZDJĘCIA"}
            </button>
            <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUploadPhoto} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {(selectedDog.gallery || []).map((photo, idx) => (
              <div key={photo} className="group relative bg-[#141416] border border-[#2A2A2D] overflow-hidden">
                <div className="aspect-square relative">
                  <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
                  {selectedDog.image_url === photo && (
                    <div className="absolute top-2 left-2 bg-[#D4AF37] text-[#0B0B0D] text-[9px] px-2 py-0.5 font-semibold flex items-center gap-1">
                      <Star className="w-3 h-3" /> GŁÓWNE
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all" />
                  <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                    {selectedDog.image_url !== photo && (
                      <button onClick={() => setMainPhoto(photo)}
                        className="w-8 h-8 bg-[#D4AF37]/90 flex items-center justify-center text-[#0B0B0D] hover:bg-[#D4AF37]" title="Ustaw jako główne">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => handleDeletePhoto(photo)}
                      className="w-8 h-8 bg-red-900/80 border border-red-700/50 flex items-center justify-center text-red-300 hover:bg-red-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {uploading && <UploadOverlay />}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
// VIDEOS TAB
// ═══════════════════════════════════════
const VideosTab = () => {
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVideos = async () => {
    try { const r = await fetch(`${API}/videos`); setVideos(await r.json()); } catch {}
  };
  useEffect(() => { fetchVideos(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
      try { await fetch(`${API}/admin/videos/upload`, { method: "POST", body: fd }); } catch {}
    }
    setUploading(false);
    fileInputRef.current.value = "";
    fetchVideos();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć ten film?")) return;
    await fetch(`${API}/admin/videos/${id}`, { method: "DELETE" });
    setVideos(p => p.filter(x => x.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-[#B8B8B8]/40 text-sm">{videos.length} filmów</span>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="flex items-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold tracking-wider hover:bg-[#E8C547] disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? "WGRYWANIE..." : "DODAJ FILM"}
        </button>
        <input ref={fileInputRef} type="file" multiple accept="video/*" className="hidden" onChange={handleUpload} />
      </div>

      {videos.length === 0 && (
        <div className="text-center py-20 text-[#B8B8B8]/40">
          <Film className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-sm tracking-wider">Brak filmów</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map(video => (
          <div key={video.id} className="group relative bg-[#141416] border border-[#2A2A2D] overflow-hidden">
            <div className="aspect-video relative">
              <video src={video.src} className="w-full h-full object-cover" muted preload="metadata" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <button onClick={() => handleDelete(video.id)}
                  className="w-10 h-10 bg-red-900/80 border border-red-700/50 flex items-center justify-center text-red-300 hover:bg-red-800">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-3">
              <p className="text-xs text-[#B8B8B8]/60 truncate">{video.label}</p>
            </div>
          </div>
        ))}
      </div>
      {uploading && <UploadOverlay />}
    </div>
  );
};

const UploadOverlay = () => (
  <div className="fixed inset-0 bg-[#0B0B0D]/80 flex items-center justify-center z-50">
    <div className="bg-[#141416] border border-[#D4AF37]/30 px-8 py-6 text-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-[#F5F5F5] text-sm tracking-wider">Wgrywanie...</p>
    </div>
  </div>
);

export default KennelAdminGallery;
